/**
 * CREATIVE DETONATOR - Signup Page Logic (Feature Location)
 *
 * Handles new account creation with email/password.
 * Phase 1: Includes invitation code validation for invite-only mode.
 */

import { supabase } from './client.js';
import { redirectIfLoggedIn } from './guard.js';

// ============================================================================
// CONFIGURATION
// ============================================================================

// API base URL - uses config.js value or production fallback
const API_BASE = window.API_CONFIG_URL || 'https://api.creativedetonator.com';

// ============================================================================
// INITIALIZATION
// ============================================================================

// Check if already logged in
const wasRedirected = await redirectIfLoggedIn();
if (wasRedirected) {
  throw new Error('Redirecting to app');
}

// ============================================================================
// DOM ELEMENTS
// ============================================================================

const form = document.getElementById('signup-form');
const nameInput = document.getElementById('name');
const emailInput = document.getElementById('email');
const passwordInput = document.getElementById('password');
const confirmPasswordInput = document.getElementById('confirm-password');
const errorEl = document.getElementById('error-message');
const submitBtn = document.getElementById('submit-btn');
const togglePasswordBtn = document.getElementById('toggle-password');
const toggleConfirmBtn = document.getElementById('toggle-confirm');
const passwordStrengthBar = document.getElementById('password-strength-bar');
const passwordHint = document.getElementById('password-hint');
const invitationGroup = document.getElementById('invitation-group');
const invitationCodeInput = document.getElementById('invitation-code');

// Track if invitation is required
let invitationRequired = false;

// ============================================================================
// UI HELPERS
// ============================================================================

function showError(message) {
  errorEl.textContent = message;
  errorEl.classList.remove('hidden');
}

function hideError() {
  errorEl.classList.add('hidden');
}

function setLoading(loading) {
  submitBtn.disabled = loading;
  submitBtn.classList.toggle('loading', loading);
}

// ============================================================================
// PASSWORD VISIBILITY TOGGLE
// ============================================================================

function setupPasswordToggle(toggleBtn, input) {
  if (!toggleBtn || !input) return;

  toggleBtn.addEventListener('click', () => {
    const isPassword = input.type === 'password';
    input.type = isPassword ? 'text' : 'password';
    toggleBtn.textContent = isPassword ? 'HIDE' : 'SHOW';
  });
}

setupPasswordToggle(togglePasswordBtn, passwordInput);
setupPasswordToggle(toggleConfirmBtn, confirmPasswordInput);

// ============================================================================
// PASSWORD STRENGTH INDICATOR
// ============================================================================

function checkPasswordStrength(password) {
  let score = 0;

  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z\d]/.test(password)) score++;

  return score;
}

function updatePasswordStrength() {
  const password = passwordInput.value;
  const strength = checkPasswordStrength(password);

  // Remove all classes
  passwordStrengthBar.classList.remove('weak', 'medium', 'strong');

  if (password.length === 0) {
    passwordStrengthBar.style.width = '0%';
    passwordHint.textContent = 'Use 8+ characters with a mix of letters and numbers';
    return;
  }

  if (strength <= 2) {
    passwordStrengthBar.classList.add('weak');
    passwordHint.textContent = 'Weak - add more characters and variety';
  } else if (strength <= 3) {
    passwordStrengthBar.classList.add('medium');
    passwordHint.textContent = 'Medium - consider adding special characters';
  } else {
    passwordStrengthBar.classList.add('strong');
    passwordHint.textContent = 'Strong password';
  }
}

passwordInput.addEventListener('input', updatePasswordStrength);

// ============================================================================
// INVITATION CODE CHECK
// ============================================================================

/**
 * Check if invitation code is required and show/hide the field
 */
async function checkInvitationRequired() {
  try {
    const response = await fetch(`${API_BASE}/api/invitation-required`);
    const data = await response.json();

    if (data.success && data.required) {
      invitationRequired = true;
      invitationGroup.style.display = 'block';
      invitationCodeInput.required = true;
      console.log('[Signup] Invitation code required');
    } else {
      invitationRequired = false;
      invitationGroup.style.display = 'none';
      invitationCodeInput.required = false;
      console.log('[Signup] No invitation code required');
    }
  } catch (err) {
    console.warn('[Signup] Could not check invitation requirement:', err.message);
    // Default to not requiring invitation if check fails
    invitationRequired = false;
  }
}

/**
 * Validate the invitation code with the backend
 */
async function validateInvitationCode(code) {
  try {
    const response = await fetch(`${API_BASE}/api/validate-invitation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code })
    });
    const data = await response.json();
    return data.valid === true;
  } catch (err) {
    console.error('[Signup] Invitation validation error:', err);
    return false;
  }
}

// Check invitation requirement on page load
checkInvitationRequired();

// ============================================================================
// FORM SUBMISSION
// ============================================================================

form.addEventListener('submit', async (e) => {
  e.preventDefault();
  hideError();

  const name = nameInput.value.trim();
  const email = emailInput.value.trim();
  const password = passwordInput.value;
  const confirmPassword = confirmPasswordInput.value;
  const invitationCode = invitationCodeInput?.value?.trim() || '';

  // ========== VALIDATION ==========

  // Invitation code validation (if required)
  if (invitationRequired) {
    if (!invitationCode) {
      showError('Please enter your invitation code.');
      invitationCodeInput.focus();
      return;
    }

    setLoading(true);
    const isValidCode = await validateInvitationCode(invitationCode);
    if (!isValidCode) {
      showError('Invalid invitation code. Please check and try again.');
      invitationCodeInput.focus();
      setLoading(false);
      return;
    }
    setLoading(false);
  }

  // Name validation
  if (!name || name.length < 2) {
    showError('Please enter a valid display name (at least 2 characters).');
    nameInput.focus();
    return;
  }

  // Email validation
  if (!email) {
    showError('Please enter your email address.');
    emailInput.focus();
    return;
  }

  // Password validation
  if (password.length < 8) {
    showError('Password must be at least 8 characters.');
    passwordInput.focus();
    return;
  }

  // Confirm password
  if (password !== confirmPassword) {
    showError('Passwords do not match.');
    confirmPasswordInput.focus();
    return;
  }

  // Password strength check (warn but allow)
  const strength = checkPasswordStrength(password);
  if (strength <= 1) {
    showError('Please choose a stronger password with mixed characters.');
    passwordInput.focus();
    return;
  }

  setLoading(true);

  // ========== SIGNUP ==========

  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          display_name: name
        },
        emailRedirectTo: `${window.location.origin}/features/auth/pages/callback.html`
      }
    });

    if (error) {
      // Handle specific errors
      if (error.message.includes('already registered')) {
        throw new Error('This email is already registered. Please login instead.');
      }
      throw error;
    }

    console.log('[Signup] Success');

    // Check if email confirmation is required
    if (data.user && !data.session) {
      // Email confirmation required - redirect to confirmation page
      window.location.href = 'confirm-email.html';
    } else if (data.session) {
      // Auto-confirmed (dev mode or disabled confirmation)
      window.location.href = '/features/generator/index.html';
    }

  } catch (error) {
    console.error('[Signup] Error:', error);
    showError(error.message || 'Signup failed. Please try again.');
    setLoading(false);
  }
});

// ============================================================================
// AUTO-FOCUS NAME INPUT
// ============================================================================

nameInput.focus();
